import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../Loader/Loader';
import { useAppConfig } from '../../AppConfigProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import '../../ModernTable.css';

const PushQB = ({ load, setLoad, uId }) => {
  const appConfig = useAppConfig();
  const userID = uId;
  const [bill, setBills] = useState([]);
  const [isPublishingAll, setIsPublishingAll] = useState(false);
  const [processingBillId, setProcessingBillId] = useState(null);
  
  const isBusy = isPublishingAll || processingBillId !== null;
  const fetchBills = useCallback(async (showLoader = true) => {
    if (showLoader) setLoad(true);
    try {
      const response = await axios.get(`${appConfig.backendUrl}api/v1/bill/RetriveAllDocs/${userID}`);
      console.log("response", response.data);
      setBills(response.data);
    } catch (error) {
      console.error("Error fetching bills:", error);
      toast.error("Failed to fetch bills");
    } finally {
      if (showLoader) setLoad(false);
    }
  }, [appConfig.backendUrl, userID, setLoad]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);


  const PublishAll = async () => {
    try {
      const isServiceRunning = await checkServiceAvailable();

      if (!isServiceRunning) {
        toast.error("DocuSync service is not running. Please start the application and run the service before.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
        return;
      }

      setIsPublishingAll(true);

      for (const item of bill) {
        console.log("Item", item);
        setProcessingBillId(item.id);
        try {
          const response = await axios.post(`${appConfig.pushLocalUrl}api/v1/qb/publishBillToQB`, item);
          console.log("response", response.data);
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          toast.success(`${item.invoiceNo}: ${response.data}`, {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
          });
        } catch (err) {
          console.log(err);
          toast.error(`${item.invoiceNo}: ${err.response?.data || err.message}`, {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
          });
        }
        // Fetch silently to update the table immediately without a full screen loader!
        await fetchBills(false);
      }
    } catch (error) {
      console.error("Publish All error:", error);
    } finally {
      setIsPublishingAll(false);
      setProcessingBillId(null);
    }
  }

  const checkServiceAvailable = async () => {
    try {
      await axios.get(`${appConfig.pushLocalUrl}api/v1/qb/health`);
      return true;
    } catch (error) {
      return false;
    }
  };

  const publishthisBill = async (item) => {
    try {
      const isServiceRunning = await checkServiceAvailable();

      if (!isServiceRunning) {
        toast.error("DocuSync service is not running. Please start the application and run the service before.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
        return;
      }

      setProcessingBillId(item.id);
      const response = await axios.post(
        `${appConfig.pushLocalUrl}api/v1/qb/publishBillToQB`,
        item
      );

      await new Promise(resolve => setTimeout(resolve, 1000));

      toast.success(`${item.invoiceNo}: ${response.data}`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });

      await fetchBills(false);

    } catch (err) {
      console.log(err);
      toast.error(`${item.invoiceNo}: ${err.response?.data || err.message}`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
    } finally {
      setProcessingBillId(null);
    }
  }

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="pushqb-container">
          <div className="page-header">
            <h1 className="page-title">
              <FontAwesomeIcon icon={faCloudUploadAlt} />
              Push to QuickBooks
            </h1>
            <p className="page-subtitle">Publish extracted bills directly to your QuickBooks account</p>
          </div>

          <div className="table-responsive">
            {bill.length > 0 ? (
              <table className='modern-table'>
                <thead>
                  <tr>
                    <th>Bill ID</th>
                    <th>Document Name</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    bill.map((item) => {
                      return (
                        <tr key={item.id}>
                          <td data-label="Bill ID"><div className="id-cell">{item.id}</div></td>
                          <td data-label="Document Name">{item.invoiceNo}</td>
                          <td data-label="Status">
                            <span className={`status-badge ${item.publishStatus.toLowerCase()}`}>
                              {item.publishStatus}
                            </span>
                          </td>
                          <td data-label="Action">
                            <button 
                              className='btn-premium primary' 
                              onClick={() => publishthisBill(item)}
                              disabled={isBusy}
                              style={{ 
                                opacity: isBusy ? (processingBillId === item.id ? 0.8 : 0.4) : 1, 
                                cursor: isBusy ? 'not-allowed' : 'pointer' 
                              }}
                            >
                              {processingBillId === item.id 
                                ? "Publishing... ⏳" 
                                : (isPublishingAll ? "Waiting... ⏳" : "Publish to QB")}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody></table>
            ) : (
              <div className="empty-state">
                <p>Nothing to push right now.</p>
              </div>
            )}
          </div>

          {bill.length > 0 && (
            <button 
              className='btn-premium success' 
              onClick={PublishAll}
              disabled={isBusy}
              style={{ 
                opacity: isBusy ? 0.6 : 1, 
                cursor: isBusy ? 'not-allowed' : 'pointer' 
              }}
            >
              {isPublishingAll ? "Publishing All... ⏳" : "Publish All Pending Bills"}
            </button>
          )}
        </div>
      )}
      <ToastContainer />
    </>
  )
}

export default PushQB